import React, { useEffect, useMemo, useState } from 'react'
import * as XLSX from 'xlsx'
import {
  CCard,
  CCardBody,
  CRow,
  CCol,
  CButton,
  CLoadingButton,
  CFormInput,
  CFormCheck,
  CAlert,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CSpinner,
  CBadge,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilCloudUpload,
  cilCloudDownload,
  cilLowVision,
  cilCheckAlt,
  cilCheckCircle,
  cilArrowLeft,
  cilFile,
  cilInfo,
  cilCheck,
  cilX,
  cilWarning,
  cilCopy,
  cilEnvelopeClosed,
} from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import CommonService from 'src/service/CommonService'
import GeneralService from 'src/service/GeneralService'
import ExampleCsv from '../../assets/MemberJoinCode.csv'
import PreviewEmailTemplete from './PreviewEmailTemplete'

const WIZARD_STEPS = ['Upload File', 'Validate People', 'Review & Import']

// Solid badge colors (not the bg-*-subtle/text-*-emphasis utilities) so the label always
// renders with guaranteed contrast, plus an accent used to tint/border each table row.
const RESULT_META = {
  valid: { label: 'Valid', color: 'success', icon: cilCheckCircle, accent: '#198754' },
  invalid_email: { label: 'Invalid Email', color: 'danger', icon: cilWarning, accent: '#dc3545' },
  missing_email: { label: 'Missing Email', color: 'danger', icon: cilWarning, accent: '#dc3545' },
  missing_name: { label: 'Missing Name', color: 'warning', icon: cilWarning, accent: '#ffc107' },
  duplicate_in_file: {
    label: 'Duplicate in File',
    color: 'warning',
    icon: cilCopy,
    accent: '#ffc107',
  },
  already_invited: {
    label: 'Already Invited',
    color: 'info',
    icon: cilEnvelopeClosed,
    accent: '#0dcaf0',
  },
  already_joined: {
    label: 'Already Joined',
    color: 'secondary',
    icon: cilCheckAlt,
    accent: '#6c757d',
  },
}

const tintStyle = (hex, alphaHex) => ({ backgroundColor: `${hex}${alphaHex}` })

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Excludes rows that need correction or are duplicates outright; "already invited/joined"
// rows are excluded only while the admin has the skip-checkbox on.
const defaultIncluded = (result, skipHandled) => {
  if (
    result === 'duplicate_in_file' ||
    result === 'invalid_email' ||
    result === 'missing_email' ||
    result === 'missing_name'
  ) {
    return false
  }
  if (skipHandled && (result === 'already_invited' || result === 'already_joined')) return false
  return true
}

const guessColumn = (headers, patterns) =>
  headers.find((h) =>
    patterns.some((p) =>
      String(h)
        .toLowerCase()
        .replace(/[^a-z]/g, '')
        .includes(p),
    ),
  ) || ''

const MiniStepper = ({ current, maxReached, onStepClick }) => (
  <div className="d-flex align-items-center mb-4 flex-wrap">
    {WIZARD_STEPS.map((label, idx) => {
      const n = idx + 1
      const done = n < current
      const active = n === current
      const clickable = n <= maxReached
      return (
        <React.Fragment key={label}>
          <div
            className="d-flex align-items-center gap-2"
            style={{ cursor: clickable ? 'pointer' : 'default', opacity: clickable ? 1 : 0.5 }}
            onClick={() => clickable && onStepClick(n)}
          >
            <div
              className={`d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${
                done ? 'bg-success text-white' : active ? 'bg-primary text-white' : 'bg-body border'
              }`}
              style={{ width: 28, height: 28, fontSize: 13 }}
            >
              {done ? <CIcon icon={cilCheckAlt} size="sm" /> : n}
            </div>
            <span className={`small ${active ? 'fw-semibold text-primary' : ''}`}>{label}</span>
          </div>
          {n < WIZARD_STEPS.length && (
            <div
              className={`mx-2 ${done ? 'bg-success' : 'bg-secondary-subtle'}`}
              style={{ height: 2, width: 32 }}
            />
          )}
        </React.Fragment>
      )
    })}
  </div>
)

const InfoBox = ({ children, color = 'info' }) => (
  <CAlert color={color} className="d-flex align-items-start gap-2 border-0">
    <CIcon icon={cilInfo} className="flex-shrink-0 mt-1" />
    <div>{children}</div>
  </CAlert>
)

// Explicit hex (not the bg-*-subtle/text-*-emphasis utilities, which render invisible text in
// this theme) so the tinted background, icon chip and number all keep guaranteed contrast.
const TONE_HEX = {
  success: '#198754',
  danger: '#dc3545',
  warning: '#b45309',
  info: '#087990',
  secondary: '#495057',
}

const StatTile = ({ label, value, tone, icon }) => {
  const hex = TONE_HEX[tone] || TONE_HEX.secondary
  return (
    <div
      className="rounded-4 p-3 flex-fill d-flex align-items-center gap-3"
      style={{ ...tintStyle(hex, '14'), border: `1px solid ${hex}33` }}
    >
      <div
        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
        style={{ width: 38, height: 38, backgroundColor: hex, color: '#fff' }}
      >
        <CIcon icon={icon} size="sm" />
      </div>
      <div>
        <div className="fs-4 fw-bold lh-1" style={{ color: hex }}>
          {value}
        </div>
        <div className="small text-medium-emphasis">{label}</div>
      </div>
    </div>
  )
}

// "Import List" tab of the Invitation step: upload a CSV/Excel file, review each row
// (missing/invalid email, missing name, duplicates, already invited/joined guests) and fix
// any of it inline, then commit only the rows the admin left included.
const ImportGuestWizard = ({ fetchData, onSummaryChange }) => {
  const [wizardStep, setWizardStep] = useState(1)
  const [maxWizardStep, setMaxWizardStep] = useState(1)

  const [fileName, setFileName] = useState('')
  const [fileRowCount, setFileRowCount] = useState(0)
  const [busy, setBusy] = useState(false)

  const [rows, setRows] = useState([]) // { row, first_name, last_name, email, result, included }
  const [skipHandled, setSkipHandled] = useState(true)
  const [editingRow, setEditingRow] = useState(null)
  const [editDraft, setEditDraft] = useState({ first_name: '', last_name: '', email: '' })

  const [importing, setImporting] = useState(false)

  const [modalIsOpen, setIsOpen] = useState(false)
  const [templeteDetails, setTempleteDetails] = useState('')
  const previewTemplete = () => {
    GeneralService.getEmailTemplateDetail(72).then((res) => {
      if (res.status === 200) setTempleteDetails(res.data)
    })
    setIsOpen(true)
  }

  const goToStep = (n) => {
    setWizardStep(n)
    setMaxWizardStep((m) => Math.max(m, n))
  }

  const resetAll = () => {
    setWizardStep(1)
    setMaxWizardStep(1)
    setFileName('')
    setFileRowCount(0)
    setRows([])
  }

  const handleFile = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setBusy(true)
    const reader = new FileReader()
    reader.onload = (evt) => {
      let json = []
      let rawHeaders = []
      try {
        const isCsv = /\.csv$/i.test(file.name)
        const workbook = XLSX.read(evt.target.result, { type: isCsv ? 'string' : 'array' })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        json = XLSX.utils.sheet_to_json(sheet, { defval: '' })
        rawHeaders = json.length ? Object.keys(json[0]) : []
      } catch (err) {
        ToastComponent('Could not read this file. Please check the format and try again.', 'error')
        setBusy(false)
        return
      }

      if (!rawHeaders.length) {
        ToastComponent('This file has no rows to import.', 'error')
        setBusy(false)
        return
      }

      const detected = {
        first_name: guessColumn(rawHeaders, ['firstname', 'first', 'fname']),
        last_name: guessColumn(rawHeaders, ['lastname', 'last', 'lname']),
        email: guessColumn(rawHeaders, ['email', 'mail']),
      }
      const memberData = json.map((r) => ({
        first_name: detected.first_name ? String(r[detected.first_name] ?? '').trim() : '',
        last_name: detected.last_name ? String(r[detected.last_name] ?? '').trim() : '',
        email: detected.email ? String(r[detected.email] ?? '').trim() : '',
      }))

      CommonService.validateGameGuestImport({ memberData })
        .then((res) => {
          if (res.status !== 200) throw new Error(res.message)
          const nextRows = (res.data?.rows || []).map((r) => ({
            ...r,
            included: defaultIncluded(r.result, skipHandled),
          }))
          setFileName(file.name)
          setFileRowCount(json.length)
          setRows(nextRows)
          goToStep(2)
        })
        .catch(() => ToastComponent('Could not validate this file. Please try again.', 'error'))
        .finally(() => setBusy(false))
    }
    if (/\.csv$/i.test(file.name)) reader.readAsText(file)
    else reader.readAsArrayBuffer(file)
    e.target.value = ''
  }

  const toggleSkipHandled = (checked) => {
    setSkipHandled(checked)
    setRows((prev) =>
      prev.map((r) =>
        r.result === 'already_invited' || r.result === 'already_joined'
          ? { ...r, included: !checked }
          : r,
      ),
    )
  }

  const toggleRowIncluded = (row) => {
    setRows((prev) => prev.map((r) => (r.row === row ? { ...r, included: !r.included } : r)))
  }

  const startEdit = (r) => {
    setEditingRow(r.row)
    setEditDraft({ first_name: r.first_name, last_name: r.last_name, email: r.email })
  }

  const cancelEdit = () => {
    setEditingRow(null)
    setEditDraft({ first_name: '', last_name: '', email: '' })
  }

  const saveEdit = (row) => {
    const first_name = editDraft.first_name.trim()
    const last_name = editDraft.last_name.trim()
    const email = editDraft.email.trim()
    const emailKey = email.toLowerCase()

    let result
    if (email === '') {
      result = 'missing_email'
    } else if (!EMAIL_RE.test(email)) {
      result = 'invalid_email'
    } else if (rows.some((r) => r.row !== row && r.email.trim().toLowerCase() === emailKey)) {
      result = 'duplicate_in_file'
    } else if (first_name === '' || last_name === '') {
      result = 'missing_name'
    } else {
      result = 'valid'
    }

    setRows((prev) =>
      prev.map((r) =>
        r.row === row
          ? { ...r, first_name, last_name, email, result, included: result === 'valid' }
          : r,
      ),
    )
    cancelEdit()
  }

  const summary = useMemo(() => {
    const valid = rows.filter((r) => r.result === 'valid').length
    const needsAttention = rows.filter(
      (r) =>
        r.result === 'invalid_email' || r.result === 'missing_email' || r.result === 'missing_name',
    ).length
    const alreadyInvited = rows.filter((r) => r.result === 'already_invited').length
    const alreadyJoined = rows.filter((r) => r.result === 'already_joined').length
    return { total: rows.length, valid, needsAttention, alreadyInvited, alreadyJoined }
  }, [rows])

  const readyRows = rows.filter((r) => r.included)

  // Mirrors this step's stats up to the host page (Game Entry Settings), which shows them in
  // its own "Import Summary" sidebar card instead of duplicating that layout in here.
  useEffect(() => {
    if (!onSummaryChange) return
    if (wizardStep === 1 || !rows.length) {
      onSummaryChange(null)
      return
    }
    onSummaryChange({
      fileRows: rows.length,
      ready: readyRows.length,
      excluded: rows.length - readyRows.length,
      needsCorrection: summary.needsAttention,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardStep, rows, summary.needsAttention])

  useEffect(() => () => onSummaryChange?.(null), []) // eslint-disable-line react-hooks/exhaustive-deps

  const runImport = () => {
    if (!readyRows.length) return
    setImporting(true)
    const memberData = readyRows.map((r) => ({
      first_name: r.first_name,
      last_name: r.last_name,
      email: r.email,
    }))
    CommonService.commitGameGuestImport({ memberData })
      .then((res) => {
        if (res.status !== 200) throw new Error(res.message)
        ToastComponent(res.message, 'success')
        fetchData?.()
        resetAll()
      })
      .catch((err) => ToastComponent(err?.message || 'Import failed. Please try again.', 'error'))
      .finally(() => setImporting(false))
  }

  const renderUpload = () => (
    <CCard className="border-secondary-subtle rounded-4">
      <CCardBody>
        <div className="fw-semibold fs-6 mb-1">Upload a CSV or Excel file</div>
        <div className="small mb-3">
          Each row should include the guest&rsquo;s first name, last name and email address.
        </div>
        <CFormInput type="file" accept=".csv,.xlsx,.xls" onChange={handleFile} disabled={busy} />
        {busy && (
          <div className="d-flex align-items-center gap-2 small text-medium-emphasis mt-2">
            <CSpinner size="sm" /> Reading and validating your file&hellip;
          </div>
        )}
        <div className="text-medium-emphasis small mt-2">
          Need a template first?{' '}
          <a
            href={ExampleCsv}
            download="Member-Join-Code-File.csv"
            target="_blank"
            rel="noreferrer"
            className="text-medium-emphasis text-decoration-none"
          >
            <CIcon icon={cilCloudDownload} size="sm" className="me-1" />
            Download Sample CSV
          </a>
          {' · '}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault()
              previewTemplete()
            }}
            className="text-medium-emphasis text-decoration-none"
          >
            <CIcon icon={cilLowVision} size="sm" className="me-1" />
            Preview Email Template
          </a>
        </div>
      </CCardBody>
    </CCard>
  )

  const renderValidate = () => (
    <>
      <CCard className="border-secondary-subtle rounded-4 mb-3">
        <CCardBody className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <CIcon icon={cilFile} className="text-primary" />
            <div>
              <div className="fw-semibold">{fileName}</div>
              <div className="small text-medium-emphasis">{fileRowCount} rows</div>
            </div>
          </div>
          <CButton color="secondary" variant="outline" size="sm" onClick={resetAll}>
            Replace File
          </CButton>
        </CCardBody>
      </CCard>

      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="Valid" value={summary.valid} tone="success" icon={cilCheckCircle} />
        <StatTile
          label="Needs Attention"
          value={summary.needsAttention}
          tone="warning"
          icon={cilWarning}
        />
        <StatTile
          label="Already Invited"
          value={summary.alreadyInvited}
          tone="info"
          icon={cilEnvelopeClosed}
        />
        <StatTile
          label="Already Joined"
          value={summary.alreadyJoined}
          tone="secondary"
          icon={cilCheckAlt}
        />
      </div>
      <CCard className="border-secondary-subtle rounded-4 overflow-hidden">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0 ge-import-table">
            <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
              <CTableRow>
                <CTableHeaderCell className="ps-4">Row</CTableHeaderCell>
                <CTableHeaderCell>First Name</CTableHeaderCell>
                <CTableHeaderCell>Last Name</CTableHeaderCell>
                <CTableHeaderCell>Email Address</CTableHeaderCell>
                <CTableHeaderCell>Result</CTableHeaderCell>
                <CTableHeaderCell>Action</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {rows.map((r) => {
                const meta = RESULT_META[r.result] || RESULT_META.valid
                const isEditing = editingRow === r.row
                const canToggle = r.result === 'already_invited' || r.result === 'already_joined'
                return (
                  <CTableRow
                    key={r.row}
                    className={r.included ? '' : 'opacity-50'}
                    style={{
                      ...tintStyle(meta.accent, '0d'),
                      borderLeft: `3px solid ${meta.accent}`,
                    }}
                  >
                    <CTableDataCell className="ps-4">{r.row}</CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormInput
                          size="sm"
                          value={editDraft.first_name}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, first_name: e.target.value }))
                          }
                        />
                      ) : (
                        r.first_name || '—'
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormInput
                          size="sm"
                          value={editDraft.last_name}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, last_name: e.target.value }))
                          }
                        />
                      ) : (
                        r.last_name || '—'
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormInput
                          size="sm"
                          autoFocus
                          value={editDraft.email}
                          onChange={(e) => setEditDraft((d) => ({ ...d, email: e.target.value }))}
                        />
                      ) : (
                        r.email || '—'
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      <CBadge
                        color={meta.color}
                        shape="rounded-pill"
                        className="d-inline-flex align-items-center gap-1 px-2 py-1"
                      >
                        <CIcon icon={meta.icon} size="sm" />
                        {meta.label}
                      </CBadge>
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <div className="d-flex gap-1">
                          <CButton size="sm" color="primary" onClick={() => saveEdit(r.row)}>
                            <CIcon icon={cilCheck} />
                          </CButton>
                          <CButton
                            size="sm"
                            color="secondary"
                            variant="outline"
                            onClick={cancelEdit}
                          >
                            <CIcon icon={cilX} />
                          </CButton>
                        </div>
                      ) : (
                        <div className="d-flex gap-2">
                          <CButton
                            size="sm"
                            color="link"
                            className="p-0"
                            onClick={() => startEdit(r)}
                          >
                            Edit
                          </CButton>
                          {canToggle && (
                            <CButton
                              size="sm"
                              color="link"
                              className="p-0"
                              onClick={() => toggleRowIncluded(r.row)}
                            >
                              {r.included ? 'Skip' : 'Include'}
                            </CButton>
                          )}
                        </div>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                )
              })}
            </CTableBody>
          </CTable>
        </div>
      </CCard>
      <CFormCheck
        className="mt-2"
        label="Skip people already invited or joined"
        checked={skipHandled}
        onChange={(e) => toggleSkipHandled(e.target.checked)}
      />
      <div className="mt-3">
        <InfoBox>
          No emails will be sent during import. You can review all recipients before sending
          invitations.
        </InfoBox>
      </div>
      <div className="d-flex justify-content-between mt-3">
        <CButton color="secondary" variant="outline" onClick={() => goToStep(1)}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back to Upload
        </CButton>
        <CButton color="primary" disabled={!readyRows.length} onClick={() => goToStep(3)}>
          Continue to Review
        </CButton>
      </div>
    </>
  )

  const renderReview = () => {
    const excluded = rows.length - readyRows.length
    return (
      <>
        <CRow className="g-3 mb-3">
          <CCol sm={6} lg={3}>
            <StatTile label="File Rows" value={rows.length} tone="secondary" icon={cilFile} />
          </CCol>
          <CCol sm={6} lg={3}>
            <StatTile
              label="Ready to Import"
              value={readyRows.length}
              tone="success"
              icon={cilCheckCircle}
            />
          </CCol>
          <CCol sm={6} lg={3}>
            <StatTile label="Excluded" value={excluded} tone="warning" icon={cilWarning} />
          </CCol>
          <CCol sm={6} lg={3}>
            <StatTile
              label="Needs Correction"
              value={summary.needsAttention}
              tone="danger"
              icon={cilWarning}
            />
          </CCol>
        </CRow>
        <InfoBox>
          No emails will be sent during import. You can review all recipients before sending
          invitations.
        </InfoBox>
        <CCard className="border-secondary-subtle rounded-4 overflow-hidden mt-3">
          <div className="table-responsive" style={{ maxHeight: 320, overflowY: 'auto' }}>
            <CTable align="middle" hover className="mb-0 ge-import-table">
              <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
                <CTableRow>
                  <CTableHeaderCell className="ps-4">First Name</CTableHeaderCell>
                  <CTableHeaderCell>Last Name</CTableHeaderCell>
                  <CTableHeaderCell>Email Address</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {readyRows.map((r) => (
                  <CTableRow key={r.row}>
                    <CTableDataCell className="ps-4">{r.first_name || '—'}</CTableDataCell>
                    <CTableDataCell>{r.last_name || '—'}</CTableDataCell>
                    <CTableDataCell>{r.email}</CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </div>
        </CCard>
        <div className="d-flex justify-content-between mt-3">
          <CButton color="secondary" variant="outline" onClick={() => goToStep(2)}>
            <CIcon icon={cilArrowLeft} className="me-1" />
            Back
          </CButton>
          <CLoadingButton
            color="success"
            loading={importing}
            disabled={!readyRows.length}
            onClick={runImport}
          >
            <CIcon icon={cilCloudUpload} className="me-1" />
            Import {readyRows.length} Guest{readyRows.length === 1 ? '' : 's'}
          </CLoadingButton>
        </div>
      </>
    )
  }

  return (
    <>
      <style>{`
        .ge-import-table > :not(caption) > * > * { padding-top: .85rem; padding-bottom: .85rem; }
      `}</style>
      <MiniStepper current={wizardStep} maxReached={maxWizardStep} onStepClick={goToStep} />
      {wizardStep === 1 && renderUpload()}
      {wizardStep === 2 && renderValidate()}
      {wizardStep === 3 && renderReview()}
      <PreviewEmailTemplete
        IsModalOpened={modalIsOpen}
        onCloseModal={() => setIsOpen(false)}
        templeteDetails={templeteDetails}
      />
    </>
  )
}

export default ImportGuestWizard
